import { useEffect, useState } from 'react';

import { listenToSyncReports, type SyncFailureReason } from './syncMessages';
import { isSyncReportFollowed } from './useSyncStatus';
import styles from './RefusedAnswersNotice.module.css';

// A report can reach the page before the question that made the request learns its
// id, from the worker's answer. Given a moment, that question speaks for it.
const QUESTION_WAIT_MS = 500;

const NONE_REFUSED: Record<SyncFailureReason, number> = {
  refused: 0,
  'section-closed': 0,
};

function getClosedSentence(count: number) {
  // "Assignment" does for homework and pre-lecture questions alike, and the report
  // says nothing of which it was.
  return count === 1
    ? 'An answer you saved on this device was not accepted: its assignment closed before it could be sent. Your instructor can grant an extension.'
    : `${count} answers you saved on this device were not accepted: their assignment closed before they could be sent. Your instructor can grant an extension.`;
}

function getRefusedSentence(count: number) {
  return count === 1
    ? 'An answer you saved on this device could not be sent. Check its question, and save it again.'
    : `${count} answers you saved on this device could not be sent. Check their questions, and save them again.`;
}

/**
 * Speaks for a queued answer the server refused when no question on the page knows of
 * it (issue #44). A replay needs an open page for its token, so a student who saved
 * offline and closed the tab has theirs sent by whatever page they open next, and the
 * refused answer leaves the queue for good: this is all they will hear of it.
 *
 * It lives in the shell, above the router, so it names no chapter and has no link.
 */
export function RefusedAnswersNotice() {
  const [refused, setRefused] = useState(NONE_REFUSED);

  useEffect(() => {
    const told = new Set<string>();
    const timers = new Set<number>();
    const stopListening = listenToSyncReports(({ reason, requestId, type }) => {
      // Only a refusal has a reason. An entry dropped at sign-out has none, and nor
      // does anything from a worker older than this.
      if (type !== 'sync-status' || !reason || !requestId || told.has(requestId)) {
        return;
      }

      told.add(requestId);

      const timer = window.setTimeout(() => {
        timers.delete(timer);

        if (!isSyncReportFollowed(requestId)) {
          setRefused((current) => ({ ...current, [reason]: current[reason] + 1 }));
        }
      }, QUESTION_WAIT_MS);

      timers.add(timer);
    });

    return () => {
      stopListening();
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  const closedCount = refused['section-closed'];
  const refusedCount = refused.refused;

  if (closedCount === 0 && refusedCount === 0) {
    return null;
  }

  return (
    <div className={styles.notice} role="alert">
      <div className={styles.messages}>
        {closedCount > 0 ? (
          <p className={styles.message}>{getClosedSentence(closedCount)}</p>
        ) : null}
        {refusedCount > 0 ? (
          <p className={styles.message}>{getRefusedSentence(refusedCount)}</p>
        ) : null}
      </div>
      <button
        className={styles.dismiss}
        onClick={() => setRefused(NONE_REFUSED)}
        type="button"
      >
        Dismiss
      </button>
    </div>
  );
}
